import { Action, ActionType } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, of, OperatorFunction } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { toReduxError } from '@universal/errors';
import {
  ConsumerGuidesListResponse,
  ConsumerGuideDetail,
  ConsumerTestedProduct,
  ConsumerTestedProductsResponse,
} from '@appTypes/consumerTestType';
import { Actions, actions } from '@redux/modules/consumerGuides';
import { TypedEpic } from './types';

// Get all consumer guides for listing.
export const getConsumerGuidesEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_CONSUMER_GUIDES),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getConsumerGuides.request>[]
    >,
    switchMap(() =>
      fromApiRequest((signal) =>
        apiClient.get<ConsumerGuidesListResponse>(ApiType.apimNafNoApi, {
          signal,
          url: ApiPaths[ApiType.apimNafNoApi].consumertest.guides,
        }),
      ).pipe(
        map((response) => actions.getConsumerGuides.success(response.data)),
        catchError((err) => of(actions.getConsumerGuides.failure(toReduxError(err, 'Could not get guides.')))),
      ),
    ),
  );

// Get a specific consumer guide.
const getOneConsumerGuideEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_ONE_GUIDE),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getOneGuide.request>[]
    >,
    switchMap(([action]) => {
      const slug = action.payload;

      // Dispatch clearTestedProducts first, then fetch the guide
      return of(actions.clearTestedProducts()).pipe(
        concatMap(() =>
          fromApiRequest((signal) =>
            apiClient.get<ConsumerGuideDetail>(ApiType.apimNafNoApi, {
              signal,
              url: `commonarticles/${slug}`,
            }),
          ).pipe(
            map((response) => actions.getOneGuide.success(response.data)),
            catchError((err) => of(actions.getOneGuide.failure(toReduxError(err, 'Could not get guide')))),
          ),
        ),
      );
    }),
  );

// Get products from a specific consumer guide.
const getTestedProductsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_TESTED_PRODUCTS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getTestedProducts.request>[]
    >,
    switchMap(([action]) => {
      const id = action.payload;

      // Dispatch clearTestedProducts before fetching new tested products
      return of(actions.clearTestedProducts()).pipe(
        concatMap(() =>
          fromApiRequest((signal) =>
            apiClient.get<ConsumerTestedProductsResponse>(ApiType.apimNafNoApi, {
              signal,
              url: `consumertest/products/${id}`,
            }),
          ).pipe(
            map((response) => actions.getTestedProducts.success(response.data)),
            catchError((err) =>
              of(actions.getTestedProducts.failure(toReduxError(err, 'Could not get tested products'))),
            ),
          ),
        ),
      );
    }),
  );

// Get one product. Used to showcase on tested product page.
const getOneProductEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_ONE_PRODUCT),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getOneProduct.request>[]
    >,
    switchMap(([action]) => {
      const slug = action.payload;

      return fromApiRequest((signal) =>
        apiClient.get<ConsumerTestedProduct>(ApiType.apimNafNoApi, {
          signal,
          url: `commonarticles/${slug}`,
        }),
      ).pipe(
        map((response) => actions.getOneProduct.success(response.data)),
        catchError((err) => of(actions.getOneProduct.failure(toReduxError(err, 'Could not get product')))),
      );
    }),
  );

// Get related products from a specific consumer guide.
const getRelatedProductsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_RELATED_PRODUCTS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getRelatedProducts.request>[]
    >,
    switchMap(([action]) => {
      const id = action.payload;
      return of(actions.clearTestedProducts()).pipe(
        concatMap(() =>
          fromApiRequest((signal) =>
            apiClient.get<ConsumerTestedProductsResponse>(ApiType.apimNafNoApi, {
              signal,
              url: `consumertest/products/${id}`,
              params: { minimal: 'true' },
            }),
          ).pipe(
            map((response) => actions.getRelatedProducts.success(response.data)),
            catchError((err) =>
              of(actions.getRelatedProducts.failure(toReduxError(err, 'Could not get related products'))),
            ),
          ),
        ),
      );
    }),
  );

export const epics: TypedEpic[] = [
  getConsumerGuidesEpic,
  getOneConsumerGuideEpic,
  getTestedProductsEpic,
  getRelatedProductsEpic,
  getOneProductEpic,
];
